/**
 * rum-user
 *
 * Helper puro para asociar la sesión de Datadog RUM con el usuario autenticado.
 * Sin esto, las sesiones y los Session Replay aparecen como usuario anónimo.
 *
 * Hay dos tipos de usuario en la app:
 *  - `candidate`: entra por magic link, su email vive en `sessionStorage.user_email`.
 *  - `admin`: entra por Cognito + Google, su perfil vive en `localStorage.admin_user`.
 *
 * Los atributos extra viajan a Datadog como `usr.*` y son filtrables desde la UI.
 * `usr.id` es el SHA-256 del email normalizado; `usr.email` y `usr.name` nunca se envían.
 *
 * Nota: `setUser` no es retroactivo. Los eventos previos al login (landing,
 * pantalla de auth) quedan anónimos, lo cual es el comportamiento esperado.
 */

import { datadogRum } from '@datadog/browser-rum';
import { envConfig } from '../../../env-config';

export type RumUserType = 'candidate' | 'admin';

export interface RumUser {
  email: string;
  userType: RumUserType;
  role?: string;
  testId?: string;
}

const CANDIDATE_EMAIL_KEY = 'user_email';
const CANDIDATE_TEST_ID_KEY = 'test_id';
const ADMIN_USER_KEY = 'admin_user';
/** Valor que usa `authenticateAdmin` como testId para marcar el login de admin. */
export const ADMIN_TEST_ID = 'admin';

/**
 * Datadog puede no estar inicializado: `initDatadog` en `main.ts` corre en
 * paralelo al bootstrap de Angular y no arranca si faltan las credenciales.
 * Llamar a la API igual no rompe, pero encolaría eventos que nunca se envían.
 */
export function isRumReady(): boolean {
  try {
    return datadogRum.getInitConfiguration() !== undefined;
  } catch {
    return false;
  }
}

/**
 * Fire-and-forget RUM custom action. No `isRumReady` guard on purpose: the SDK buffers
 * events fired before `init`. Never put emails, tokens or full URLs in `ctx`.
 */
export function trackRumAction(name: string, ctx?: Record<string, string | number | boolean | undefined>): void {
  try {
    datadogRum.addAction(name, ctx);
  } catch {
    // Telemetry never breaks the flow.
  }
}

/**
 * SHA-256 (lowercase hex) of the trimmed, lowercased email. Used as `usr.id` so the
 * raw email never reaches Datadog. Uses Web Crypto, which is async and only available
 * in secure contexts; callers must handle rejection.
 */
export async function hashRumEmail(email: string): Promise<string> {
  const bytes = new TextEncoder().encode(email.trim().toLowerCase());
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

/** Bumped by every identify/clear so a slow hash cannot overwrite a newer user state. */
let identifyGeneration = 0;

/**
 * Fire-and-forget: hashing is async, so the user is set once the digest resolves.
 * If hashing is unavailable the session stays anonymous (never falls back to the raw email).
 */
export async function identifyRumUser(user: RumUser): Promise<void> {
  if (!isRumReady() || !user.email) return;
  const generation = ++identifyGeneration;
  try {
    const id = await hashRumEmail(user.email);
    if (generation !== identifyGeneration) return;
    datadogRum.setUser({
      id,
      userType: user.userType,
      ...(user.role ? { role: user.role } : {}),
      ...(user.testId ? { testId: user.testId } : {}),
    });
  } catch {
    // Telemetry never breaks the flow.
  }
}

export function clearRumUser(): void {
  if (!isRumReady()) return;
  identifyGeneration++;
  datadogRum.clearUser();
}

/**
 * Reidentifica al usuario leyendo el storage. Necesario en dos escenarios:
 *  - Recarga de página con sesión ya activa (no vuelve a pasar por el login).
 *  - Login que ocurrió antes de que `datadogRum.init()` terminara: el identify
 *    de ese momento fue un no-op, pero el storage ya quedó escrito.
 */
export async function restoreRumUserFromStorage(): Promise<void> {
  if (!isRumReady()) return;

  // `sessionStorage` es por pestaña, así que refleja al usuario de ESTA pestaña
  // y tiene prioridad sobre `admin_user` (que puede quedar de otra sesión).
  // El login de admin por magic link guarda `test_id = 'admin'`; en ese caso el
  // perfil bueno es el de `admin_user`, con su rol.
  const candidateEmail = safeRead(() => sessionStorage.getItem(CANDIDATE_EMAIL_KEY));
  const candidateTestId = safeRead(() => sessionStorage.getItem(CANDIDATE_TEST_ID_KEY));
  if (candidateEmail && candidateTestId !== ADMIN_TEST_ID) {
    await identifyRumUser({
      email: candidateEmail,
      userType: 'candidate',
      testId: candidateTestId ?? undefined,
    });
    return;
  }

  const storedAdmin = safeRead(() => localStorage.getItem(ADMIN_USER_KEY));
  if (storedAdmin) {
    try {
      const admin = JSON.parse(storedAdmin) as { email?: string; role?: string };
      if (admin?.email) {
        await identifyRumUser({
          email: admin.email,
          userType: 'admin',
          role: admin.role,
        });
      }
    } catch {
      // JSON corrupto: se queda anónimo, no vale la pena romper el arranque.
    }
  }
}

// ── Contexto global de prueba ──
//
// A diferencia de `usr.*`, el contexto global se adjunta a TODOS los eventos RUM
// (views, errores, resources, actions, replay), así que permite aislar todo lo
// que ocurrió durante una prueba concreta, no solo lo atribuible al usuario.
// Se consulta en Datadog como `@context.test_id` / `@context.session_id`.
//
// Recordatorio operativo: el atributo se envía desde el primer evento y la
// búsqueda por texto funciona de inmediato, pero para usarlo en desplegables de
// filtro o agrupaciones hay que crear la faceta una vez en RUM Explorer.

const TEST_ID_CONTEXT_KEY = 'test_id';
const SESSION_ID_CONTEXT_KEY = 'session_id';

export interface RumTestContext {
  testId?: string;
  sessionId?: string;
}

/** Añade o actualiza las claves indicadas; las omitidas quedan intactas. */
export function setRumTestContext(context: RumTestContext): void {
  if (!isRumReady()) return;
  if (context.testId) {
    datadogRum.setGlobalContextProperty(TEST_ID_CONTEXT_KEY, context.testId);
  }
  if (context.sessionId) {
    datadogRum.setGlobalContextProperty(SESSION_ID_CONTEXT_KEY, context.sessionId);
  }
}

/**
 * Se llama al salir de la prueba. Sin esto, el contexto de una prueba terminada
 * se pegaría a los eventos posteriores de la misma pestaña.
 */
export function clearRumTestContext(): void {
  if (!isRumReady()) return;
  datadogRum.removeGlobalContextProperty(TEST_ID_CONTEXT_KEY);
  datadogRum.removeGlobalContextProperty(SESSION_ID_CONTEXT_KEY);
}

const DEFAULT_DD_SITE = 'datadoghq.com';

/**
 * Link directo a la sesión de Datadog RUM actual, para pegar en un registro
 * (ej. una alerta) y que un admin pueda acceder al replay sin tener que
 * buscar la sesión a mano. `undefined` si RUM no está inicializado o la
 * sesión todavía no tiene `session_id` (contexto interno no listo).
 */
export function getDatadogReplayLink(): string | undefined {
  if (!isRumReady()) return undefined;
  const context = datadogRum.getInternalContext();
  if (!context?.session_id) return undefined;
  return `https://app.${DEFAULT_DD_SITE}/rum/replay/sessions/${context.session_id}`;
}

/**
 * Link a Datadog RUM Explorer filtrado por `@context.test_id` — el mismo
 * atributo global que `setRumTestContext({ testId })` tagea en TODOS los
 * eventos RUM del candidato durante una prueba (quiz.component.ts). A
 * diferencia de `getDatadogReplayLink`, este no depende de RUM estar
 * inicializado en el navegador que lo llama: lo construye el panel admin
 * para ver la sesión de OTRO usuario (el candidato), ya pasada.
 */
export function getDatadogTestExplorerLink(assignmentId: string): string {
  const site = envConfig['VITE_DD_SITE'] || DEFAULT_DD_SITE;
  const query = encodeURIComponent(`@context.test_id:${assignmentId}`);
  return `https://app.${site}/rum/explorer?query=${query}`;
}

function safeRead(read: () => string | null): string | null {
  try {
    return read();
  } catch {
    return null;
  }
}
